import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ProductForm, ProductFormData } from '../components/products';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { usePhoneFormGutter } from '../hooks/usePhoneFormGutter';

interface ProductCreateScreenProps {
  catalogueId: string;
}

export const ProductCreateScreen: React.FC<ProductCreateScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const phoneFormGutter = usePhoneFormGutter();
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(false);

  const listId: string | undefined = routeParams?.listId;

  const productCreateInitialData = routeParams?.scannedBarcode ? { barcode: routeParams.scannedBarcode } : undefined;

  const finishAndNavigate = async (productId?: string) => {
    if (listId && productId) {
      try {
        await apiService.linkDataListRecords(listId, [productId]);
      } catch (err) {
        console.error('[ProductCreateScreen] Failed to link new product to data list:', err);
      }
      navigateTo('data-list-records', listId);
    } else {
      navigateTo('products-list', catalogueId);
    }
  };

  const handleSubmit = async (formData: ProductFormData) => {
    try {
      setLoading(true);

      // Convert blob URLs and file URIs to base64 data URIs before sending
      // Process images sequentially to avoid overwhelming the browser with large base64 conversions
      const processedImages: ProductImage[] = [];
      
      for (const img of formData.images) {
        // If already a Firebase Storage URL or other valid URL, use as-is
        if (!img.url.startsWith('blob:') && !img.url.startsWith('file://') && !img.url.startsWith('content://')) {
          processedImages.push(img);
          continue;
        }

        // Convert blob URL or file URI to base64 data URI
        try {
          let blob: Blob;
          
          if (img.url.startsWith('blob:') && typeof window !== 'undefined') {
            // Fetch blob URL and convert to blob
            const response = await fetch(img.url);
            blob = await response.blob();
          } else {
            // For file:// or content:// URIs, we need to use React Native's FileSystem
            // For now, skip these and let them be uploaded separately after product creation
            console.warn('[ProductCreateScreen] File URI detected, will upload separately:', img.url);
            processedImages.push(img); // Return as-is, will be handled separately
            continue;
          }

          // Convert blob to base64
          const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          processedImages.push({
            ...img,
            url: base64Data, // Replace blob URL with base64 data URI
          });
        } catch (error: any) {
          console.error('[ProductCreateScreen] Error converting image to base64:', error);
          // Return original image - backend will handle or skip it
          processedImages.push(img);
        }
      }

      // Separate processed images (base64) from server URLs and file URIs that couldn't be converted
      const base64Images = processedImages.filter(img => img.url.startsWith('data:'));
      const serverImages = processedImages.filter(img => 
        !img.url.startsWith('data:') && !img.url.startsWith('blob:') && 
        !img.url.startsWith('file://') && !img.url.startsWith('content://')
      );
      const fileUriImages = processedImages.filter(img => 
        img.url.startsWith('file://') || img.url.startsWith('content://')
      );

      // Transform form data to API format
      // Include base64 images and server images (backend will upload base64 to Firebase Storage)
      const productData = {
        catalogueIds: formData.catalogueIds,
        productType: formData.productType,
        name: formData.name,
        description: formData.description,
        brand: formData.brand,
        sku: formData.sku,
        barcode: formData.barcode,
        price: formData.price,
        currency: formData.currency,
        salePrice: formData.salePrice,
        costPrice: formData.costPrice,
        inventoryQuantity: formData.inventoryQuantity,
        inventoryTracking: formData.inventoryTracking,
        lowStockThreshold: formData.lowStockThreshold,
        minimumOrderQuantity: formData.minimumOrderQuantity ?? null,
        images: [...base64Images, ...serverImages],
        categoryIds: formData.categoryIds,
        hasVariants: formData.hasVariants,
        variants: formData.variants,
        availability: formData.availability,
        condition: formData.condition,
        weight: formData.weight,
        dimensions: formData.dimensions,
        unitId: formData.unitId,
        priceUnit: formData.priceUnit,
        digitalDelivery: formData.digitalDelivery,
        serviceDelivery: formData.serviceDelivery,
        serviceLocation: formData.serviceLocation,
        addons: formData.addons,
        pricingZones: formData.pricingZones,
      };

      // Create product first
      const response = await apiService.createProduct(catalogueId, productData);

      if (response.statusCode === 1000 && response.data) {
        const productId = response.data._id || response.data.id;

        // Upload file URI images (mobile) that couldn't be converted to base64
        if (fileUriImages.length > 0 && productId) {
          try {
            const uploadPromises = fileUriImages.map(async (image, index) => {
              try {
                // Use the blob URL directly - the API service will handle conversion
                const fileUri = image.url;
                let fileName = `image_${Date.now()}_${index}.jpg`;
                let fileType = 'image/jpeg';

                // Try to determine file type from blob URL (for web)
                if (fileUri.startsWith('blob:') && typeof window !== 'undefined') {
                  try {
                    const response = await fetch(fileUri);
                    const blob = await response.blob();
                    fileType = blob.type || 'image/jpeg';
                    const extension = fileType.split('/')[1] || 'jpg';
                    fileName = `image_${Date.now()}_${index}.${extension}`;
                  } catch (e) {
                    console.warn('Could not determine file type from blob, using defaults');
                  }
                }

                const uploadResult = await apiService.uploadProductImage(
                  productId,
                  fileUri,
                  fileName,
                  fileType
                );

                if (uploadResult.statusCode === 1000 && uploadResult.data) {
                  return {
                    url: uploadResult.data.url,
                    alt: image.alt || uploadResult.data.alt || fileName,
                    isPrimary: image.isPrimary,
                    order: image.order,
                  };
                } else {
                  throw new Error(uploadResult.errors?.[0]?.message || 'Failed to upload image');
                }
              } catch (error: any) {
                console.error('Error uploading image:', error);
                // Don't fail the entire operation if one image fails
                return null;
              }
            });

            const uploadedImages = await Promise.all(uploadPromises);
            const successfulUploads = uploadedImages.filter(img => img !== null);

            // If some images failed to upload, show a warning
            if (successfulUploads.length < localImages.length) {
              showNotification(
                'warning',
                'Partial Upload',
                `Product created successfully, but ${localImages.length - successfulUploads.length} image(s) failed to upload.`
              );
              setTimeout(() => finishAndNavigate(productId), 2000);
              return;
            } else {
              showNotification(
                'success',
                'Success',
                'Product created successfully with all images uploaded.'
              );
              setTimeout(() => finishAndNavigate(productId), 1500);
              return;
            }
          } catch (error: any) {
            console.error('Error uploading images:', error);
            showNotification(
              'warning',
              'Images Upload Failed',
              'Product created successfully, but some images failed to upload. You can add them later.'
            );
            setTimeout(() => finishAndNavigate(productId), 2000);
            return;
          }
        } else {
          showNotification('success', 'Success', 'Product created successfully.');
          setTimeout(() => finishAndNavigate(productId), 1500);
          return;
        }

      } else {
        const errorMessage =
          response.errors?.[0]?.message || response.message || 'Failed to create product';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating product:', error);
      // Extract error message - authenticatedRequest throws Error with message property
      // and may also have errors array attached
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          'Failed to create product';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('products');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="New Product" onBack={handleCancel} />}
    >
      <View style={styles.screen}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={[
            styles.contentContainer,
            Platform.OS === 'web' && styles.contentContainerWeb,
            Platform.OS !== 'web' && styles.contentContainerMobile,
            phoneFormGutter,
          ]}
          showsVerticalScrollIndicator={false}
        >
          <ProductForm
            catalogueId={catalogueId}
            initialData={productCreateInitialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Create Product"
            loading={loading}
          />
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100, // Extra padding for mobile to avoid bottom tabs overlay
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm, // Minimal padding on web (ScreenLayout handles spacing)
  },
});

